import { AppAuthRefeshTokenKey, LocaleKey, AppAuthTokenKey, SelectedStoreIdKey, StoreTimezoneKey } from '@/consts/constant';
import type { AxiosInstance } from 'axios';
import axios from 'axios';
import { boot } from 'quasar/wrappers';
import { Cookies } from 'quasar';
import { useAuthenStore } from '@/stores/authen';
import { AUTH_ROUTE_NAME } from '@/consts/route-name';
import { clearAuthCookiesAndLocalStorage, isClient, isElectron } from '@/utils/appUtil';
import { parseRefreshResponse } from '@/utils/jwtUtil';
import type { Token } from '@/types/models';

declare module '@vue/runtime-core' {
  interface ComponentCustomProperties {
    $axios: AxiosInstance;
    $api: AxiosInstance;
  }
}

const api = axios.create({
  // baseURL: process.env.NODE_ENV == 'development' ? 'http://192.168.7.249:8080' : 'https://api.example.com',
  baseURL: import.meta.env.VITE_API_BASE_URL || '',
  withCredentials: false,
  timeout: import.meta.env.VITE_APP_TIMEOUT
    ? +import.meta.env.VITE_APP_TIMEOUT
    : 3 * 60000, // 60000 = 1 minute, 0 = no timeout
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache',
    'Pragma': 'no-cache',
    'Expires': '0',
  },
  validateStatus: (status) => status < 400, // Resolve only if the status code is less than 400
  // validateStatus: (status) => status <= 500 // Resolve only if the status code is less than 500
});


// Single-flight refresh: only ONE POST /refresh-token may be in flight at a time
// across BOTH the reactive 401 interceptor (below) and the proactive expiry-check
// path (authenStore.refreshTokenProcess). Concurrent callers share the same
// in-flight promise instead of each replaying the single-use refresh token — which
// the backend would reject as "revoked" and force a false logout (issue #686).
let refreshPromise: Promise<Token> | null = null;

function readRefreshToken(ssrContext?: any) {
  // On the server (SSR) cookies live in the request context, not the global jar.
  const ck = process.env.SERVER && ssrContext ? Cookies.parseSSR(ssrContext) : Cookies;
  let refreshToken = ck.get(AppAuthRefeshTokenKey);
  if (isElectron() && !refreshToken && typeof window !== 'undefined') {
    refreshToken = localStorage.getItem(AppAuthRefeshTokenKey);
  }
  return refreshToken;
}

async function doRefresh(ssrContext?: any): Promise<Token> {
  const refreshToken = readRefreshToken(ssrContext);
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  // Bare instance with NO interceptors so a 401 here never recurses back into
  // this refresh flow.
  const refreshApi = axios.create({
    baseURL: import.meta.env.VITE_API_BASE_URL || '',
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  const response = await refreshApi.post('/api/v1/auth/refresh-token', {
    refreshToken,
  });

  // Parse via shared helper so every caller agrees on the token shape.
  const tokenData = parseRefreshResponse(response.data);
  if (!tokenData) {
    throw new Error('Invalid refresh token response format');
  }
  return tokenData;
}

export function refreshTokenSingleFlight(ssrContext?: any): Promise<Token> {
  // On the server, module-level state is shared across requests (and cookies must
  // come from the SSR context) — so never cache there, just refresh directly.
  // The single-flight dedupe is a browser concern: the multi-request/multi-tab
  // race of issue #686 only happens client-side.
  if (process.env.SERVER) {
    return doRefresh(ssrContext);
  }

  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = doRefresh().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
}

export default boot(({ app, redirect, store, router }) => {
  const authenStore = useAuthenStore(store);
  // Check if interceptors are already set up
  const isServer = process.env.SERVER;
  if (!isServer) {
    api.interceptors.request.use(
      async (config) => {
        // Add Authorization header to all requests
        // In Electron, use localStorage as cookies may not work
        let token = Cookies.get(AppAuthTokenKey);
        if (isElectron() && !token) {
          token = localStorage.getItem(AppAuthTokenKey);
        }
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        config.headers['Accept-Language'] = Cookies.get(LocaleKey);

        if (isClient()) {
          const storeId = localStorage.getItem(SelectedStoreIdKey);
          if (storeId) {
            config.headers['X-STORE-ID'] = storeId;
          }

          const timezone = localStorage.getItem(StoreTimezoneKey);
          if (timezone) {
            config.headers['X-TIMEZONE'] = timezone;
          }
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      },
    );
    api.interceptors.response.use(
      (response) => {
        return response;
      },
      async (error) => {
        const originalRequest = error.config;
        let refreshToken = Cookies.get(AppAuthRefeshTokenKey);
        if (isElectron() && !refreshToken) {
          refreshToken = localStorage.getItem(AppAuthRefeshTokenKey);
        }
        // Skip refresh logic for the refresh-token endpoint itself to avoid an
        // infinite refresh loop. Only clear the session when the refresh token is
        // genuinely rejected (401/403); transient errors (network/timeout/5xx)
        // must NOT log the user out — the refresh token may still be valid.
        if (originalRequest.url?.includes('/api/v1/auth/refresh-token')) {
          const refreshStatus = error.response?.status;
          if (refreshStatus === 401 || refreshStatus === 403) {
            authenStore.logout();
            clearAuthCookiesAndLocalStorage(Cookies);
            router.push({ name: AUTH_ROUTE_NAME.SIGNIN });
          }
          return Promise.reject(error);
        }

        if (
          refreshToken &&
          error.response &&
          error.response.status === 401 &&
          !originalRequest._retry
        ) {
          // Mark as retried up-front so a fresh 401 on the replayed request won't
          // start another refresh cycle (avoids a refresh loop).
          originalRequest._retry = true;

          try {
            // Shared single-flight: concurrent 401s (and the proactive path) all
            // await the SAME refresh instead of each replaying the refresh token.
            const tokenData = await refreshTokenSingleFlight();

            await authenStore.setRefreshTokenCookie(undefined, tokenData);
            originalRequest.headers.Authorization = 'Bearer ' + tokenData.accessToken;
            return api(originalRequest);
          } catch (errRefresh) {
            // Only clear the session when the refresh token is genuinely rejected
            // (401/403). Transient errors (network/timeout/5xx) must NOT log the
            // user out — the refresh token may still be valid.
            const refreshStatus = (errRefresh as { response?: { status?: number } })?.response?.status;
            if (refreshStatus === 401 || refreshStatus === 403) {
              authenStore.logout();
              clearAuthCookiesAndLocalStorage(Cookies);
              router.push({ name: AUTH_ROUTE_NAME.SIGNIN });
            }

            return Promise.reject(errRefresh);
          }
        }

        // If no refresh token or different error, redirect to auth on 401
        // if (error.response && error.response.status === 401) {
        //   authenStore.logout();
        //   Cookies.remove(AppAuthTokenKey);
        //   Cookies.remove(AppAuthRefeshTokenKey);
        //   router.push({ name: AUTH_ROUTE_NAME.SIGNIN });
        // }

        return Promise.reject(error);
      },
    );
  }
  app.config.globalProperties.$axios = axios;
  app.config.globalProperties.$api = api;
});

export { api };
